import { GoogleOAuthProvider } from '@react-oauth/google';
import { UserProvider, useUser } from './context/UserProvider';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { ModelExplorer } from './components/ModelExplorer';
import { UserInfoCard } from './components/UserInfoCard';
import { AppSettings } from './AppSettings';
import { Container, Card, Row, Col, Form, Alert } from 'react-bootstrap';
import './App.scss';
import { DownloadUnityPackageButton } from './components/Buttons/DownloadPackageButton';
import { Cnt } from './Cnt.ThreeDeeCrate.SDK/Cnt.ThreeDeeCrate.SDK';
import { StyleChooserModal } from './components/StyleChooserModal';
import { BrowseModels } from './components/BrowseModels';


// ── helpers ───────────────────────────────────────────────────────────────────
const styleNames = Object.keys(Cnt.ThreeDeeCrate.SDK.GraphicalStyleType).filter(k => isNaN(Number(k)));
const DEFAULT_STYLE = styleNames[1] || 'Standard'; // ✅ first enum name (fallback to "Standard" if enum empty for any reason)

function toGraphicalStyle(raw?: string): Cnt.ThreeDeeCrate.SDK.GraphicalStyleType {
  const def = Cnt.ThreeDeeCrate.SDK.GraphicalStyleType[DEFAULT_STYLE as keyof typeof Cnt.ThreeDeeCrate.SDK.GraphicalStyleType]
    ?? Cnt.ThreeDeeCrate.SDK.GraphicalStyleType.Standard;
  if (!raw) return def;
  const n = Number(raw);
  if (!Number.isNaN(n) && n in Cnt.ThreeDeeCrate.SDK.GraphicalStyleType) return n as any;
  const byName = styleNames.find(k => k.toLowerCase() === raw.toLowerCase());
  return byName
    ? (Cnt.ThreeDeeCrate.SDK.GraphicalStyleType as any)[byName]
    : def;
}

// Wrapper that reads params and PASSES STYLE AS A PROP to ModelExplorer
const ModelExplorerRoute: React.FC = () => {
  const { style } = useParams();
  const styleEnum: Cnt.ThreeDeeCrate.SDK.GraphicalStyleType = toGraphicalStyle(style);
  return <ModelExplorer styleValue={styleEnum} />;
};

// ── layout ────────────────────────────────────────────────────────────────────
function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  // Current categoryPath = everything after "/{style}/"
  const segments = location.pathname.split('/').filter(Boolean); // ["Style","Root--..."]
  const currentStyle = segments[0] ?? DEFAULT_STYLE;               // ✅ default to first style, not "Standard"
  const currentCategoryPath = segments.slice(1).join('/') || 'Root';

  const handleStyleChange = (nextStyleName: string) => {
    navigate(`/${encodeURIComponent(nextStyleName)}/${currentCategoryPath}`, { replace: true });
  };

  return (
    <Container className="my-4">
      
      <StyleChooserModal />
      <Row className="align-items-center">
        <Col className="d-flex justify-content-start">
          <Form.Select
            aria-label="Style"
            value={currentStyle}
            onChange={(e) => handleStyleChange(e.target.value)}
            style={{ maxWidth: 200 }}
          >
            {styleNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </Form.Select>
        </Col>

        <Col className="text-center">
          <img src="/logo.png" alt="Logo" height={50} />
        </Col>

        <Col className="d-flex justify-content-end">
          <UserInfoCard />
        </Col>
      </Row>

      <br />
<BrowseModels/>
      <br />

      <Card className="shadow-lg">
        <Card.Body>
          <Outlet />
        </Card.Body>
      </Card>
      <br />
      <Card className="shadow-lg">
        <Card.Body>
          <Row className="align-items-center">
            <Col className="d-flex justify-content-start">
              <span>{AppSettings.ApplicationName} - {AppSettings.Version}</span>
            </Col>
            <Col>
              <span className="text-warning">
                Contact:{' '}
                <a href="mailto:corey_stjacques@hotmail.com">corey_stjacques@hotmail.com</a>
              </span>
            </Col>
            {(user?.Subscription == Cnt.ThreeDeeCrate.SDK.SubscriptionType.PremiumModelSubscription || user?.IsAdmin) && (
                          <Col className="d-flex justify-content-end">
              <DownloadUnityPackageButton packageUrl={'/3DCrate.unitypackage'} />
            </Col>
            )}

          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
}

// ── app ───────────────────────────────────────────────────────────────────────
function App() {
  return (
    <GoogleOAuthProvider clientId={AppSettings.GoogleClientId}>
      <UserProvider>
<BrowserRouter>
  <Routes>
    {/* Root now shows AppLayout so the modal can appear */}
    <Route path="/" element={<AppLayout />}>
      {/* no content at / — the modal handles navigation */}
      <Route index element={<></>} />
    </Route>

    {/* Style + category routes inside the same layout */}
    <Route path="/:style" element={<AppLayout />}>
      <Route index element={<Navigate to="Root" replace />} />
      <Route path=":categoryPath" element={<ModelExplorerRoute />} />
    </Route>

    {/* Fallback */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
</BrowserRouter>

      </UserProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
